import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '@env/environment';
import { LoggerService } from '@app/core/services/logger.service';
import {
  DEFAULT_OUTAGE_MESSAGE,
  DEFAULT_OUTAGE_TITLE,
} from '@app/shared/outage-modal/outage-modal.component';

export interface ServiceNotice {
  title: string;
  message: string;
}

// Wording used when the flag turns a notice on without text. It is the in-portal
// outage modal's wording, imported rather than copied, so a user sees one
// message before and after sign-in and the two cannot drift apart.
export const DEFAULT_SERVICE_NOTICE: ServiceNotice = {
  title: DEFAULT_OUTAGE_TITLE,
  message: DEFAULT_OUTAGE_MESSAGE,
};

// Same limits as the backend (rn-portal /service-notice) and the in-portal
// modal, so every surface shows the same text. The backend already truncates;
// this keeps the page safe if that ever changes or a proxy serves something
// else. Longer values are truncated, not rejected, so the notice still shows.
export const MAX_NOTICE_TITLE_LENGTH = 120;
export const MAX_NOTICE_MESSAGE_LENGTH = 1000;

// The notice check sits in front of every sign-in, so it must never hold one
// up for long. Past this, sign-in proceeds as if there were no notice.
export const SERVICE_NOTICE_TIMEOUT_MS = 2000;

/**
 * Turns the backend's /service-notice response into a notice, or null when
 * there is none. Anything that is not clearly `{ active: true }` is "no
 * notice" -- a malformed response must never get in the way of signing in.
 * Text is only ever rendered by interpolation, never as HTML.
 */
export function parseServiceNotice(raw: unknown): ServiceNotice | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const body = raw as Record<string, unknown>;
  if (body['active'] !== true) {
    return null;
  }
  return {
    title: textOrDefault(
      body['title'],
      DEFAULT_SERVICE_NOTICE.title,
      MAX_NOTICE_TITLE_LENGTH,
    ),
    message: textOrDefault(
      body['message'],
      DEFAULT_SERVICE_NOTICE.message,
      MAX_NOTICE_MESSAGE_LENGTH,
    ),
  };
}

// Array.from splits on code points, so truncation never cuts a surrogate pair
// (an emoji at the cut-off) in half.
function textOrDefault(
  value: unknown,
  fallback: string,
  maxLength: number,
): string {
  return typeof value === 'string' && value.trim() !== ''
    ? Array.from(value.trim()).slice(0, maxLength).join('')
    : fallback;
}

/**
 * Reads the current service notice (outage or maintenance) from rn-portal's
 * unauthenticated GET /service-notice, which is driven by the `outage-notice`
 * LaunchDarkly flag. Used where there is no signed-in user and so no
 * client-side LaunchDarkly: the login page.
 */
@Injectable({ providedIn: 'root' })
export class ServiceNoticeService {
  private readonly url = `${environment.portalURLBase}/service-notice`;

  constructor(private http: HttpClient) {}

  /** Emits once: the active notice, or null. Never errors. */
  getActiveNotice(): Observable<ServiceNotice | null> {
    return this.http.get<unknown>(this.url).pipe(
      timeout(SERVICE_NOTICE_TIMEOUT_MS),
      map((raw) => parseServiceNotice(raw)),
      catchError((error) => {
        LoggerService.log('ServiceNoticeService', error);
        return of(null);
      }),
    );
  }
}
